import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Upload, Check, ChevronRight, ChevronLeft, User, BookOpen, FileText, AlertTriangle, ExternalLink } from 'lucide-react';

const STEPS = [
  { id: 1, title: 'Personal Details', icon: User },
  { id: 2, title: 'Document Upload', icon: FileText },
  { id: 3, title: 'Policy Approval', icon: AlertTriangle },
];

const InputField = ({ label, required, ...props }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
      {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </label>
    <input
      className="w-full rounded-xl px-4 py-2.5 text-sm transition-all"
      style={{ background: '#fff', border: '1px solid var(--card-border)', color: 'var(--text)' }}
      required={required}
      {...props}
    />
  </div>
);

const SelectField = ({ label, options, required, ...props }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
      {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </label>
    <select
      className="w-full rounded-xl px-4 py-2.5 text-sm transition-all"
      style={{ background: '#fff', border: '1px solid var(--card-border)', color: 'var(--text)' }}
      required={required}
      {...props}
    >
      <option value="">Select...</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const RadioField = ({ label, name, options, value, onChange, required }) => (
  <div className="sm:col-span-2">
    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
      {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </label>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {options.map(opt => {
        const isSelected = value === opt;
        return (
          <label
            key={opt}
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer text-xs font-bold transition-all select-none"
            style={{
              background: isSelected ? 'rgba(79,70,229,0.06)' : '#fff',
              border: isSelected ? '2px solid var(--primary)' : '1px solid var(--card-border)',
              color: isSelected ? 'var(--primary)' : 'var(--text)',
            }}
          >
            <input
              type="radio"
              name={name}
              value={opt}
              checked={isSelected}
              onChange={() => onChange(opt)}
              className="hidden"
            />
            <div
              className="w-3.5 h-3.5 rounded-full flex items-center justify-center border transition-all"
              style={{
                borderColor: isSelected ? 'var(--primary)' : 'var(--card-border)',
                borderWidth: isSelected ? '4.5px' : '1px',
                background: '#fff'
              }}
            />
            <span>{opt}</span>
          </label>
        );
      })}
    </div>
  </div>
);

const POLICY_VERSION = 'v1.0';

const POLICY_LINKS = [
  { id: 'terms', label: 'Terms of Service', path: '/terms' },
  { id: 'privacy-policy', label: 'Privacy Policy', path: '/privacy-policy' },
  { id: 'refund-policy', label: 'Refund Policy', path: '/refund-policy' },
];

const FIELD_LABELS = {
  fullName: 'Student Name',
  academicYear: 'Academic Year',
  dob: 'Date of Birth',
  email: 'Email ID',
  mobile: 'Student Contact Number',
  community: 'Community',
  communityOther: 'Community Details',
  parentName: 'Parent Name',
  parentOccupation: 'Parent Occupation',
  parentMobile: 'Parent Contact Number',
  homeTown: 'Home Town',
  pin: 'Pincode',
  country: 'Country',
  state: 'State',
  district: 'District',
  districtOther: 'District Name',
  aadhar: 'Aadhar Number',
  gender: 'Gender',
  genderOther: 'Gender Details',
  isPwd: 'Person with Disability',
  disabilityType: 'Disability Type',
  disabilityTypeOther: 'Disability Type Details',
  disabilityPercentage: 'Disability Percentage',
  minimumQualificationAgreed: 'Minimum Qualification Confirmation',
  lateralEntryAgreed: 'Lateral Entry Selection',
};

const showMissingFieldsToast = (fields) => {
  const labels = fields.map((field) => FIELD_LABELS[field] || field);
  toast.error(`Please complete: ${labels.join(', ')}`, { id: 'application-missing-fields' });
};

const getEditableAadharValue = (value) => {
  const text = String(value || '').trim();
  if (!text || text.startsWith('enc:v1:')) return '';
  return text.replace(/\D/g, '').slice(0, 12);
};

const getRecordId = (record) => {
  if (!record || typeof record !== 'object') return record || '';
  return record._id || record.id || '';
};

const appendIfPresent = (formData, key, value) => {
  if (value !== undefined && value !== null && value !== '') {
    formData.append(key, value);
  }
};

const GridRadioField = ({ label, name, options, value, onChange, required }) => (
  <div className="sm:col-span-2">
    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
      {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </label>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-52 overflow-y-auto p-3 border rounded-xl" style={{ borderColor: 'var(--card-border)', background: '#fcfcfc' }}>
      {options.map(opt => {
        const isSelected = value === opt;
        return (
          <label
            key={opt}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-[11px] font-bold transition-all select-none"
            style={{
              background: isSelected ? 'rgba(79,70,229,0.06)' : '#fff',
              border: isSelected ? '2px solid var(--primary)' : '1px solid var(--card-border)',
              color: isSelected ? 'var(--primary)' : 'var(--text-muted)',
            }}
          >
            <input
              type="radio"
              name={name}
              value={opt}
              checked={isSelected}
              onChange={() => onChange(opt)}
              className="hidden"
            />
            <div
              className="w-3 h-3 rounded-full flex items-center justify-center border transition-all"
              style={{
                borderColor: isSelected ? 'var(--primary)' : 'var(--card-border)',
                borderWidth: isSelected ? '3.5px' : '1px',
                background: '#fff'
              }}
            />
            <span className="truncate">{opt}</span>
          </label>
        );
      })}
    </div>
  </div>
);

const MinimumQualificationAgreement = ({ qualification, checked, onChange, required }) => (
  <div className="sm:col-span-2">
    <div
      className="rounded-xl border p-4"
      style={{ background: '#fff', borderColor: 'var(--card-border)' }}
    >
      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-muted)' }}>
        Minimum Qualification {required && <span style={{ color: '#EF4444' }}>*</span>}
      </label>
      <p className="text-sm font-bold mb-4" style={{ color: 'var(--text)' }}>
        {qualification || 'Minimum qualification required for this program'}
      </p>

      <label
        className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all"
        style={{
          background: checked ? 'rgba(79,70,229,0.06)' : 'rgba(248,250,252,0.9)',
          border: checked ? '2px solid var(--primary)' : '1px solid var(--card-border)',
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          required={required}
          className="mt-1 h-4 w-4 rounded"
        />
        <span className="text-sm font-medium leading-6" style={{ color: checked ? 'var(--primary)' : 'var(--text)' }}>
          I agree I have the minimum qualification for this program.
        </span>
      </label>
    </div>
  </div>
);

const LateralEntryAgreement = ({ checked, onChange, required }) => (
  <div className="sm:col-span-2">
    <div
      className="rounded-xl border p-4"
      style={{ background: '#fff', borderColor: 'var(--card-border)' }}
    >
      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-muted)' }}>
        Lateral entry {required && <span style={{ color: '#EF4444' }}>*</span>}
      </label>
      <p className="text-sm font-bold mb-4" style={{ color: 'var(--text)' }}>
        If you are applying via lateral entry, select this option.
      </p>

      <label
        className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all"
        style={{
          background: checked ? 'rgba(79,70,229,0.06)' : 'rgba(248,250,252,0.9)',
          border: checked ? '2px solid var(--primary)' : '1px solid var(--card-border)',
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          required={required}
          className="mt-1 h-4 w-4 rounded"
        />
        <span className="text-sm font-medium leading-6" style={{ color: checked ? 'var(--primary)' : 'var(--text)' }}>
          I am applying through lateral entry.
        </span>
      </label>
    </div>
  </div>
);

export default function ApplicationForm() {
  const { state } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('editId');
  const [course, setCourse] = useState(state?.course || null);
  const [program, setProgram] = useState(state?.program || null);
  const minimumQualification = (
    program?.minimumQualification ||
    program?.minmumQualification ||
    course?.minimumQualification ||
    course?.minmumQualification ||
    ''
  ).trim();

  const currentYear = new Date().getFullYear();
  const ACADEMIC_YEARS = [
    `${currentYear}-${currentYear + 1}`,
    `${currentYear + 1}-${currentYear + 2}`
  ];

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [existingDocs, setExistingDocs] = useState({});
  const [hasCompletedApplication, setHasCompletedApplication] = useState(false);
  const [countryOptions, setCountryOptions] = useState(['India']);
  const [availableStates, setAvailableStates] = useState([]);
  const [districtOptions, setDistrictOptions] = useState(['Other']);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: user?.name || '',
    academicYear: '',
    dob: '',
    admissionType: 'Regular',
    email: user?.email || '', mobile: user?.mobile || '',
    community: '',
    communityOther: '',
    parentName: '',
    parentOccupation: '',
    parentMobile: '',
    homeTown: '',
    pin: '',
    country: 'India',
    state: '',
    district: '',
    districtOther: '',
    currentQualification: '',
    minimumQualificationAgreed: false,
    lateralEntryAgreed: false,
    aadhar: '',
    gender: '',
    genderOther: '',
    isPwd: '',
    disabilityType: '',
    disabilityTypeOther: '',
    disabilityPercentage: '',
    udidNumber: '',
    physicalApplicationNo: '',
    docs: { aadhar: null, previousSchoolTC: null, community: null, birthCertificate: null, marksheet10: null, marksheet12: null, diplomaCertificate: null, admissionForm: null, disabilityCertificate: null }
  });

  const update = (field, val) => setFormData(prev => ({ ...prev, [field]: val }));
  const updateDoc = (field, file) => setFormData(prev => ({ ...prev, docs: { ...prev.docs, [field]: file } }));

  useEffect(() => {
    axios.get('/api/locations/countries')
      .then(res => setCountryOptions(res.data?.length ? res.data : ['India']))
      .catch(() => setCountryOptions(['India']));
  }, []);

  useEffect(() => {
    if (!formData.country) {
      setAvailableStates([]);
      return;
    }
    axios.get('/api/locations/states', { params: { country: formData.country } })
      .then(res => setAvailableStates(res.data || []))
      .catch(() => setAvailableStates([]));
  }, [formData.country]);

  useEffect(() => {
    if (!formData.country || !formData.state) {
      setDistrictOptions(['Other']);
      return;
    }
    axios.get('/api/locations/districts', {
      params: { country: formData.country, state: formData.state },
    })
      .then(res => {
        const options = res.data?.length ? res.data : ['Other'];
        setDistrictOptions(formData.district && !options.includes(formData.district)
          ? [formData.district, ...options]
          : options);
      })
      .catch(() => setDistrictOptions(['Other']));
  }, [formData.country, formData.state]);

  const lookupPincode = async (value) => {
    const pin = String(value || '').replace(/\D/g, '').slice(0, 6);
    setFormData(prev => ({ ...prev, pin }));
    if (pin.length !== 6) return;

    setPincodeLoading(true);
    try {
      const res = await axios.get(`/api/locations/pincode/${pin}`);
      const data = res.data || {};
      const district = data.district || data.city || '';
      const state = data.state || '';

      setFormData(prev => ({
        ...prev,
        pin,
        country: data.country || 'India',
        state: state || prev.state,
        district: district || prev.district,
        districtOther: '',
      }));

      if (district) {
        setDistrictOptions(prev => prev.includes(district) ? prev : [district, ...prev]);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Pincode lookup failed');
    } finally {
      setPincodeLoading(false);
    }
  };
  const updateMinimumQualificationAgreement = (checked) => {
    setFormData(prev => ({
      ...prev,
      minimumQualificationAgreed: checked,
      lateralEntryAgreed: checked ? false : prev.lateralEntryAgreed,
      currentQualification: checked ? (minimumQualification || 'Minimum qualification confirmed') : '',
      admissionType: checked ? 'Regular' : prev.admissionType,
    }));
  };
  const updateLateralEntryAgreement = (checked) => {
    setFormData(prev => ({
      ...prev,
      lateralEntryAgreed: checked,
      minimumQualificationAgreed: checked ? false : prev.minimumQualificationAgreed,
      currentQualification: checked ? 'Lateral entry' : (prev.minimumQualificationAgreed ? prev.currentQualification : ''),
      admissionType: checked ? 'Lateral Entry' : prev.admissionType,
    }));
  };

  useEffect(() => {
    if (formData.minimumQualificationAgreed && minimumQualification && formData.currentQualification !== minimumQualification) {
      setFormData(prev => ({ ...prev, currentQualification: minimumQualification }));
    }
  }, [minimumQualification, formData.minimumQualificationAgreed, formData.currentQualification]);

  useEffect(() => {
    if (!course?.lateralEntryAccepted && formData.lateralEntryAgreed) {
      setFormData(prev => ({ ...prev, lateralEntryAgreed: false }));
    }
  }, [course?.lateralEntryAccepted, formData.lateralEntryAgreed]);

  useEffect(() => {
    let isMounted = true;

    if (!editId) {
      const preloadUserProfile = async () => {
        try {
          const [profileRes, documentsRes, myAppsRes] = await Promise.all([
            axios.get('/api/students/profile'),
            axios.get('/api/users/profile/documents'),
            axios.get('/api/applications/my')
          ]);

          if (!isMounted) return;

          const profile = profileRes.data?.profile || {};
          const profileDocuments = documentsRes.data?.profileDocuments || documentsRes.data || {};
          const limitReached = Boolean(myAppsRes.data?.hasActiveApplication);

          if (limitReached) {
            setHasCompletedApplication(true);
          }

          setFormData(prev => ({
            ...prev,
            fullName: profile.fullName || prev.fullName,
            dob: profile.dob || prev.dob,
            email: profile.email || prev.email,
            mobile: profile.mobile || prev.mobile,
            admissionType: profile.admissionType || prev.admissionType,
            community: profile.community || prev.community,
            communityOther: profile.communityOther || prev.communityOther,
            parentName: profile.parentName || prev.parentName,
            parentOccupation: profile.parentOccupation || prev.parentOccupation,
            parentMobile: profile.parentMobile || prev.parentMobile,
            homeTown: profile.homeTown || prev.homeTown,
            pin: profile.pin || prev.pin,
            country: profile.country || prev.country,
            state: profile.state || prev.state,
            district: profile.district || prev.district,
            districtOther: profile.districtOther || prev.districtOther,
            currentQualification: profile.currentQualification || prev.currentQualification,
            aadhar: getEditableAadharValue(profile.aadhar) || prev.aadhar,
            gender: profile.gender || prev.gender,
            genderOther: profile.genderOther || prev.genderOther,
            isPwd: profile.isPwd === true ? 'Yes' : (profile.isPwd === false ? 'No' : prev.isPwd),
            disabilityType: profile.disabilityType || prev.disabilityType,
            disabilityTypeOther: profile.disabilityTypeOther || prev.disabilityTypeOther,
            disabilityPercentage: profile.disabilityPercentage || prev.disabilityPercentage,
            udidNumber: profile.udidNumber || prev.udidNumber,
            physicalApplicationNo: profile.physicalApplicationNo || prev.physicalApplicationNo,
            docs: prev.docs,
          }));

          setExistingDocs(Object.fromEntries(
            Object.entries(profileDocuments).map(([docKey, value]) => [
              String(docKey).replace(/^doc_/, ''),
              typeof value === 'string' ? value : value?.url || value?.path || ''
            ])
          ));
        } catch (err) {
          console.error('Failed to preload user profile for application form:', err);
        }
      };

      preloadUserProfile();
    }

    return () => {
      isMounted = false;
    };
  }, [editId]);

  // Load draft from localStorage on mount (only for new applications, not edit modes)
  useEffect(() => {
    if (!editId) {
      const draftKey = `seatify_app_draft_${user?.email || 'guest'}`;
      try {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          setFormData(prev => ({
            ...prev,
            ...parsed,
            docs: prev.docs // Keep docs empty/default as Files can't be JSON serialized
          }));
          toast.success('Restored draft from autosave 💾', { id: 'draft-restore' });
        }
      } catch (err) {
        console.error('Failed to load application draft:', err);
      }
    }
  }, [editId, user]);

  // Autosave to localStorage on form text fields changes
  useEffect(() => {
    if (!editId) {
      const draftKey = `seatify_app_draft_${user?.email || 'guest'}`;
      const { docs, ...textData } = formData;

      // Check if at least one field has been custom-entered by checking values different from initial defaults
      const hasCustomData = Object.entries(textData).some(([k, val]) => {
        if (k === 'fullName' && val === (user?.name || '')) return false;
        if (k === 'email' && val === (user?.email || '')) return false;
        if (k === 'mobile' && val === (user?.mobile || '')) return false;
        if (k === 'admissionType' && val === 'Regular') return false;
        return val !== '';
      });

      if (hasCustomData) {
        localStorage.setItem(draftKey, JSON.stringify(textData));
      }
    }
  }, [formData, editId, user]);

  // Fetch data if editing
  useEffect(() => {
    let isMounted = true;
    if (editId) {
      const fetchEditData = async () => {
        setLoading(true);
        try {
          const res = await axios.get(`/api/applications/${editId}`);
          if (!isMounted) return;
          const app = res.data;

          setFormData(prev => ({
            ...prev,
            fullName: app.fullName || '',
            academicYear: app.academicYear || '',
            dob: app.dob || '',
            admissionType: app.admissionType || 'Regular',
            email: app.email || '',
            mobile: app.mobile || '',
            community: app.community || '',
            communityOther: app.communityOther || '',
            parentName: app.parentName || '',
            parentOccupation: app.parentOccupation || '',
            parentMobile: app.parentMobile || '',
            homeTown: app.homeTown || '',
            pin: app.pin || '',
            country: app.country || 'India',
            state: app.state || '',
            district: app.district || '',
            districtOther: app.districtOther || '',
            currentQualification: app.currentQualification || '',
            minimumQualificationAgreed: Boolean(app.currentQualification),
            aadhar: getEditableAadharValue(app.aadhar),
            gender: app.gender || '',
            genderOther: app.genderOther || '',
            isPwd: app.isPwd === true ? 'Yes' : (app.isPwd === false ? 'No' : ''),
            disabilityType: app.disabilityType || '',
            disabilityTypeOther: app.disabilityTypeOther || '',
            disabilityPercentage: app.disabilityPercentage || '',
            udidNumber: app.udidNumber || '',
            physicalApplicationNo: app.physicalApplicationNo || '',
          }));
          setExistingDocs(app.docs || {});

          // Always set a baseline course/program from the application data
          // This ensures getDocumentRequirements() works even if the live fetch fails
          setCourse({
            name: app.courseName,
            category: app.category,
            collegeName: app.collegeName,
            minimumQualification: app.currentQualification || ''
          });
          setProgram({
            name: app.programName,
            fee: app.fee,
            minimumQualification: app.currentQualification || ''
          });

          // Then try to fetch live course/program info for richer banner details if possible
          if (app.courseId) {
            try {
              const courseRes = await axios.get(`/api/courses/${app.courseId}`);
              if (!isMounted) return;
              setCourse(courseRes.data);
              setProgram(courseRes.data.programme || {
                _id: app.programId,
                name: app.programName,
                slug: app.programSlug,
                fee: app.fee,
                minimumQualification: app.currentQualification || ''
              });
            } catch (courseErr) {
              console.warn('Could not fetch live course details, using application baseline');
            }
          }
        } catch (err) {
          console.error(err);
          if (isMounted) toast.error('Failed to load application data for editing');
        } finally {
          if (isMounted) setLoading(false);
        }
      };
      fetchEditData();
    }
    return () => { isMounted = false; };
  }, [editId]);

  // Fallback for missing state
  useEffect(() => {
    if (!editId && (!course || !program)) {
      toast.error('Application context lost. Please select a course again.');
      navigate('/courses');
    }
  }, [course, program, navigate, editId]);

  const handleNext = () => {
    if (step === 1) {
      const requiredFields = [
        'fullName', 
        'academicYear',
        'dob', 
        'email', 
        'mobile', 
        'community', 
        'parentName', 
        'parentOccupation', 
        'parentMobile', 
        'homeTown', 
        'pin',
        'country',
        'state',
        'district', 
        'aadhar',
        'gender',
        'isPwd'
      ];
      const missing = requiredFields.filter(f => !formData[f]);
      if (missing.length > 0) {
        showMissingFieldsToast(missing);
        return;
      }

      const conditionalMissing = [];
      if (formData.gender === 'Others' && !formData.genderOther?.trim()) conditionalMissing.push('genderOther');
      if (formData.district === 'Other' && !formData.districtOther?.trim()) conditionalMissing.push('districtOther');
      if (formData.community === 'Others' && !formData.communityOther?.trim()) conditionalMissing.push('communityOther');
      if (formData.isPwd === 'Yes') {
        if (!formData.disabilityType) conditionalMissing.push('disabilityType');
        if (formData.disabilityType === 'Other' && !formData.disabilityTypeOther?.trim()) conditionalMissing.push('disabilityTypeOther');
        if (!formData.disabilityPercentage) conditionalMissing.push('disabilityPercentage');
      }
      if (conditionalMissing.length > 0) {
        showMissingFieldsToast(conditionalMissing);
        return;
      }

      if (course?.lateralEntryAccepted) {
        if (!formData.minimumQualificationAgreed && !formData.lateralEntryAgreed) {
          showMissingFieldsToast(['minimumQualificationAgreed', 'lateralEntryAgreed']);
          return;
        }
      } else if (!formData.minimumQualificationAgreed || !formData.currentQualification) {
        showMissingFieldsToast(['minimumQualificationAgreed']);
        return;
      }

      // Validate student mobile number - must be 10 digits only
      const mobileRegex = /^[0-9]{10}$/;
      if (!mobileRegex.test(formData.mobile)) {
        toast.error('Student contact number must be exactly 10 digits');
        return;
      }

      // Validate parent contact number - must be 10 digits only
      if (!mobileRegex.test(formData.parentMobile)) {
        toast.error('Parent contact number must be exactly 10 digits');
        return;
      }

      const pincodeRegex = /^[0-9]{6}$/;
      if (!pincodeRegex.test(formData.pin)) {
        toast.error('Pincode must be exactly 6 digits');
        return;
      }

      // Validate Aadhar Number - must be exactly 12 digits
      const aadharRegex = /^[0-9]{12}$/;
      if (!aadharRegex.test(formData.aadhar)) {
        toast.error('Aadhar Number must be exactly 12 digits');
        return;
      }
    }
    if (step < 3) setStep(step + 1);

    setTimeout(() => {
      const formTop = document.getElementById('application-form-top');
      if (formTop) {
        const offset = window.innerWidth < 768 ? 100 : 120;
        const y = formTop.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);

    setTimeout(() => {
      const formTop = document.getElementById('application-form-top');
      if (formTop) {
        const offset = window.innerWidth < 768 ? 100 : 120;
        const y = formTop.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
  };

  const getDocumentRequirements = () => {
    if (!course) return [];
    const docs = [
      { id: 'aadhar', label: 'Aadhar Card *', required: true },
      { id: 'birthCertificate', label: 'Birth Certificate', required: false },
      { id: 'community', label: 'Community Certificate', required: false },
    ];

    const category = course.category;
    const name = course.name?.toLowerCase() || '';

    if (category === 'K-12') {
      if (name.includes('grade 11') || name.includes('grade 12')) {
        docs.push({ id: 'marksheet10', label: '10th Mark Sheet', required: false });
        docs.push({ id: 'previousSchoolTC', label: 'Transfer Certificate (TC)', required: false });
      } else if (name.includes('pre kg')) {
        // No TC for Pre KG
      } else {
        docs.push({ id: 'previousSchoolTC', label: 'Transfer Certificate (TC)', required: false });
      }
    } else {
      // College categories
      docs.push({ id: 'marksheet10', label: '10th Mark Sheet', required: false });
      docs.push({ id: 'marksheet12', label: '12th Mark Sheet / Diploma Certificate', required: false });
    }

    if (formData.isPwd === 'Yes') {
      docs.push({ id: 'disabilityCertificate', label: 'Disability Certificate *', required: true });
    }

    return docs;
  };

  const validateDocumentsStep = () => {
    const requiredDocs = getDocumentRequirements().filter(d => d.required);
    const missing = requiredDocs.filter(d => !formData.docs[d.id] && !existingDocs[d.id]);

    if (missing.length > 0) {
      toast.error(`Please upload: ${missing.map(m => m.label.replace(' *', '')).join(', ')}`);
      return false;
    }

    return true;
  };

  const processSubmission = async () => {
    if (!policyAccepted) {
      toast.error('Please accept the policy acknowledgement before proceeding.');
      return;
    }

    const policyAcceptance = {
      accepted: true,
      acceptedAt: new Date().toISOString(),
      policyVersion: POLICY_VERSION,
      acceptedFrom: ['terms', 'privacy-policy', 'refund-policy']
    };

    setLoading(true);
    try {
      if (editId) {
        // Handle Edit submission (personal details + documents)
        const fd = new FormData();
        Object.entries(formData).forEach(([k, v]) => {
          if (k !== 'docs') {
            if (k === 'isPwd') {
              fd.append(k, v === 'Yes' ? 'true' : 'false');
            } else {
              fd.append(k, v);
            }
          }
        });
        Object.entries(formData.docs).forEach(([k, v]) => {
          if (v) fd.append(`doc_${k}`, v);
        });
        fd.append('policyAcceptance', JSON.stringify(policyAcceptance));

        const res = await axios.put(`/api/applications/${editId}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Details updated successfully!');
        // Redirect back to confirmation page to see updated details and download receipt
        navigate(`/confirmation/${editId}`, {
          state: { application: res.data.application, course, program },
          replace: true
        });
      } else {
        // Handle New submission
        const fd = new FormData();
        Object.entries(formData).forEach(([k, v]) => {
          if (k !== 'docs') {
            if (k === 'isPwd') {
              fd.append(k, v === 'Yes' ? 'true' : 'false');
            } else {
              fd.append(k, v);
            }
          }
        });
        Object.entries(formData.docs).forEach(([k, v]) => {
          if (v) fd.append(`doc_${k}`, v);
        });
        appendIfPresent(fd, 'institutionId', course?.institutionId || getRecordId(course?.institution));
        appendIfPresent(fd, 'institutionSlug', course?.institutionSlug || course?.institution?.slug);
        appendIfPresent(fd, 'courseId', getRecordId(course));
        appendIfPresent(fd, 'courseName', course?.name);
        appendIfPresent(fd, 'collegeName', course?.institutionName || course?.collegeName || course?.institution?.displayName || course?.institution?.collegeName || 'SNS Institutions');
        appendIfPresent(fd, 'category', course?.category);
        appendIfPresent(fd, 'programId', getRecordId(program) || course?.programmeId || getRecordId(course?.programme));
        appendIfPresent(fd, 'programSlug', program?.slug || course?.programmeSlug);
        appendIfPresent(fd, 'programName', program?.name || course?.programmeName);
        appendIfPresent(fd, 'fee', course?.fee || program?.fee);
        fd.append('policyAcceptance', JSON.stringify(policyAcceptance));

        const res = await axios.post('/api/applications', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Clear autosave draft on successful submission
        const draftKey = `seatify_app_draft_${user?.email || 'guest'}`;
        localStorage.removeItem(draftKey);

        toast.success('Application submitted! Proceeding to payment...');
        navigate(`/payment/${res.data.applicationId}`, {
          state: { application: res.data.application || res.data, course, program },
          replace: true
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to process application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const DocUpload = ({ label, field, accept }) => (
    <div className="rounded-xl p-4 transition-all cursor-pointer group"
      style={{
        border: `2px dashed ${(formData.docs[field] || existingDocs[field]) ? 'var(--primary)' : 'var(--card-border)'}`,
        background: (formData.docs[field] || existingDocs[field]) ? 'var(--primary-light)' : '#fff'
      }}>
      <label className="cursor-pointer flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
          style={{ background: (formData.docs[field] || existingDocs[field]) ? 'rgba(59,130,246,0.2)' : 'var(--bg)' }}>
          {(formData.docs[field] || existingDocs[field]) ? <Check size={18} className="text-blue-600" /> : <Upload size={18} style={{ color: 'var(--text-muted)' }} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-800 truncate">{label}</p>
          <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
            {formData.docs[field] ? formData.docs[field].name : existingDocs[field] ? 'File already uploaded (Click to change)' : `Click to upload (${accept})`}
          </p>
        </div>
        <input type="file" className="hidden" accept={accept} onChange={e => updateDoc(field, e.target.files[0])} />
      </label>
    </div>
  );

  if (course && course.active === false) {
    return (
      <div className="min-h-screen pt-8 pb-32 md:pb-8 px-4 flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm">
          <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-slate-800" style={{ fontFamily: 'Poppins' }}>Registration Closed</h2>
          <p className="text-slate-500 mb-6 font-medium">Course registration for {course?.name || 'this course'} is currently closed.</p>
          <button onClick={() => navigate('/courses')} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors">
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  if (hasCompletedApplication && !editId) {
    return (
      <div className="min-h-screen pt-8 pb-32 md:pb-8 px-4 flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm">
          <div className="w-16 h-16 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-slate-800" style={{ fontFamily: 'Poppins' }}>Limit Reached</h2>
          <p className="text-slate-500 mb-6 font-medium">You have reached the limit for this academic year.</p>
          <button onClick={() => navigate('/courses')} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors">
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-8 pb-32 md:pb-8 px-4" style={{ background: 'var(--bg)' }}>
      <div className="max-w-2xl mx-auto" id="application-form-top">

        {/* Course info banner */}
        {course && program && (
          <div className="rounded-xl p-4 mb-6 flex items-center gap-3"
            style={{ background: 'rgba(79,70,229,0.1)', border: '1px solid rgba(79,70,229,0.25)' }}>
            <BookOpen size={20} style={{ color: 'var(--primary)' }} />
            <div>
              <p className="text-sm font-semibold">{course.name} — {program.name}</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Pre Registration Fee: ₹{(course.fee || program.fee || 0)?.toLocaleString('en-IN')}</p>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center mb-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isDone = step > s.id;
            return (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all"
                    style={{
                      background: isDone ? 'var(--green)' : isActive ? 'var(--primary)' : 'var(--card)',
                      border: `2px solid ${isDone ? 'var(--green)' : isActive ? 'var(--primary)' : 'var(--card-border)'}`,
                      color: isDone || isActive ? '#fff' : 'var(--text-muted)'
                    }}>
                    {isDone ? <Check size={16} /> : <Icon size={15} />}
                  </div>
                  <span className="text-xs mt-1 font-medium hidden sm:block"
                    style={{ color: isActive ? 'var(--text)' : 'var(--text-muted)' }}>{s.title}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-0.5 mx-2 step-line"
                    style={{ background: step > s.id ? 'var(--primary)' : 'var(--card-border)' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Form Card */}
        <div className="rounded-2xl p-6 md:p-8" style={{ background: 'var(--card)', border: '1px solid var(--card-border)' }}>
          <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Poppins' }}>
            {editId ? 'Edit Personal Details' : `Step ${step}: ${STEPS[step - 1].title}`}
          </h2>

          {/* Step 1 */}
          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start grid-flow-row-dense">
              <InputField label="Student Name" value={formData.fullName} onChange={e => update('fullName', e.target.value)} required />
              <div className="space-y-3">
                <SelectField
                  label="Gender"
                  value={formData.gender}
                  onChange={e => { update('gender', e.target.value); if (e.target.value !== 'Others') update('genderOther', ''); }}
                  options={['Male', 'Female', 'Transgender', 'Others']}
                  required
                />
                {formData.gender === 'Others' && (
                  <InputField
                    label="Please Specify"
                    value={formData.genderOther}
                    onChange={e => update('genderOther', e.target.value)}
                    required
                    placeholder="Enter gender"
                  />
                )}
              </div>

              <SelectField
                label="Academic Year"
                value={formData.academicYear}
                onChange={e => update('academicYear', e.target.value)}
                options={ACADEMIC_YEARS}
                required
              />
              <div className="space-y-3">
                <SelectField
                  label="Community"
                  value={formData.community}
                  onChange={e => { update('community', e.target.value); if (e.target.value !== 'Others') update('communityOther', ''); }}
                  options={['OC', 'BC', 'BCM', 'MBC', 'DNC', 'SC', 'ST', 'Others']}
                  required
                />
                {formData.community === 'Others' && (
                  <InputField
                    label="Please Specify"
                    value={formData.communityOther}
                    onChange={e => update('communityOther', e.target.value)}
                    required
                    placeholder="Enter community name"
                  />
                )}
              </div>

              <InputField label="Student Contact Number" type="tel" value={formData.mobile} onChange={e => update('mobile', e.target.value.replace(/[^0-9]/g, '').slice(0, 10))} required pattern="[0-9]{10}" maxLength={10} />
              <InputField label="Date of Birth" type="date" value={formData.dob} onChange={e => update('dob', e.target.value)} required />

              {/* Row: Parent Name + Parent Occupation */}
              <InputField label="Parent Name" value={formData.parentName} onChange={e => update('parentName', e.target.value.replace(/[^A-Za-z\s]/g, ''))} required />
              <InputField label="Parent Occupation" value={formData.parentOccupation} onChange={e => update('parentOccupation', e.target.value)} required />

              {/* Row: Parent Contact + Home Town */}
              <InputField label="Parent Contact Number" type="tel" value={formData.parentMobile} onChange={e => update('parentMobile', e.target.value.replace(/[^0-9]/g, '').slice(0, 10))} required pattern="[0-9]{10}" maxLength={10} />
              <InputField label="Home Town" value={formData.homeTown} onChange={e => update('homeTown', e.target.value)} required />
              <InputField
                label={pincodeLoading ? 'Pincode (checking...)' : 'Pincode'}
                type="tel"
                value={formData.pin}
                onChange={e => lookupPincode(e.target.value)}
                required
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="Enter 6 digit pincode"
              />

              <SelectField
                label="Country"
                value={formData.country}
                onChange={e => setFormData(prev => ({
                  ...prev,
                  country: e.target.value,
                  state: '',
                  district: '',
                  districtOther: '',
                }))}
                options={countryOptions}
                required
              />

              {availableStates.length > 0 ? (
                <SelectField
                  label="State"
                  value={formData.state}
                  onChange={e => setFormData(prev => ({
                    ...prev,
                    state: e.target.value,
                    district: '',
                    districtOther: '',
                  }))}
                  options={availableStates}
                  required
                />
              ) : (
                <InputField
                  label="State / Province"
                  value={formData.state}
                  onChange={e => update('state', e.target.value)}
                  required
                />
              )}

              <div className="space-y-3">
                <SelectField
                  label="District"
                  value={formData.district}
                  onChange={e => { update('district', e.target.value); if (e.target.value !== 'Other') update('districtOther', ''); }}
                  options={districtOptions}
                  required
                />
                {formData.district === 'Other' && (
                  <InputField
                    label="Please Specify"
                    value={formData.districtOther}
                    onChange={e => update('districtOther', e.target.value)}
                    required
                    placeholder="Enter district name"
                  />
                )}
              </div>

              {/* Qualification agreement - full width */}
              {course?.lateralEntryAccepted ? (
                <>
                  <MinimumQualificationAgreement
                    qualification={minimumQualification}
                    checked={formData.minimumQualificationAgreed}
                    onChange={updateMinimumQualificationAgreement}
                    required={!formData.lateralEntryAgreed}
                  />
                  <LateralEntryAgreement
                    checked={formData.lateralEntryAgreed}
                    onChange={updateLateralEntryAgreement}
                    required={!formData.minimumQualificationAgreed}
                  />
                </>
              ) : (
                <MinimumQualificationAgreement
                  qualification={minimumQualification}
                  checked={formData.minimumQualificationAgreed}
                  onChange={updateMinimumQualificationAgreement}
                  required
                />
              )}

              {/* Row: Person with Disability (dropdown) */}
              <SelectField
                label="Person with Disability (PwD)?"
                value={formData.isPwd}
                onChange={e => { update('isPwd', e.target.value); if (e.target.value !== 'Yes') { update('disabilityType', ''); update('disabilityTypeOther', ''); update('disabilityPercentage', ''); update('udidNumber', ''); } }}
                options={['Yes', 'No']}
                required
              />

              {/* Disability sub-fields (conditional) */}
              {formData.isPwd === 'Yes' && (
                <>
                  {/* Row: Disability Type + (Other specify if Other) */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                      Disability Type <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <select
                      className="w-full rounded-xl px-4 py-2.5 text-sm transition-all"
                      style={{ background: '#fff', border: '1px solid var(--card-border)', color: 'var(--text)' }}
                      value={formData.disabilityType}
                      onChange={e => { update('disabilityType', e.target.value); if (e.target.value !== 'Other') update('disabilityTypeOther', ''); }}
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="Locomotor Disability">Locomotor Disability</option>
                      <option value="Visual Impairment">Visual Impairment</option>
                      <option value="Hearing Impairment">Hearing Impairment</option>
                      <option value="Intellectual Disability">Intellectual Disability</option>
                      <option value="Multiple Disabilities">Multiple Disabilities</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {formData.disabilityType === 'Other' ? (
                    <InputField
                      label="Please Specify Disability Type"
                      value={formData.disabilityTypeOther}
                      onChange={e => update('disabilityTypeOther', e.target.value)}
                      required
                      placeholder="Describe your disability type"
                    />
                  ) : (
                    <InputField
                      label="Disability Percentage (%)"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.disabilityPercentage}
                      onChange={e => update('disabilityPercentage', e.target.value)}
                      required
                    />
                  )}

                  {/* Row: Percentage (if Other type) + UDID */}
                  {formData.disabilityType === 'Other' && (
                    <InputField
                      label="Disability Percentage (%)"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.disabilityPercentage}
                      onChange={e => update('disabilityPercentage', e.target.value)}
                      required
                    />
                  )}
                  <div className={formData.disabilityType === 'Other' ? '' : 'sm:col-span-2'}>
                    <InputField
                      label="UDID Number (Unique Disability ID) — Optional"
                      type="text"
                      value={formData.udidNumber}
                      onChange={e => update('udidNumber', e.target.value)}
                    />
                  </div>
                </>
              )}

              {/* Row: Aadhar + Email */}
              <InputField label="Aadhar Number" type="text" value={formData.aadhar} onChange={e => update('aadhar', e.target.value.replace(/[^0-9]/g, '').slice(0, 12))} required pattern="[0-9]{12}" maxLength={12} placeholder="12-digit number" />
              <InputField label="Email ID" type="email" value={formData.email} onChange={e => update('email', e.target.value)} required />

            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                Upload clear scans or photos (PDF, JPG, PNG. Max 10MB).
              </p>

              {/* Grouped Admission Form Option */}
              <div className="rounded-2xl p-5 space-y-4 border border-blue-100 bg-blue-50/10 shadow-sm">
                <div className="flex items-center gap-2 pb-2 border-b border-blue-50">
                  <span className="text-xl">📝</span>
                  <h3 className="font-extrabold text-sm uppercase tracking-wider text-blue-900" style={{ fontFamily: 'Poppins' }}>
                    Admission Form Verification
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col justify-center">
                    <InputField
                      label="Application Number (Optional)"
                      value={formData.physicalApplicationNo}
                      onChange={e => update('physicalApplicationNo', e.target.value)}
                      placeholder="Enter physical application no."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                      Upload Photo Copy (Optional)
                    </label>
                    <DocUpload
                      label="Upload Photo Copy"
                      field="admissionForm"
                      accept=".pdf,image/*"
                    />
                  </div>
                </div>
              </div>

              {/* Other Documents */}
              {getDocumentRequirements().map(doc => (
                <DocUpload
                  key={doc.id}
                  label={doc.label}
                  field={doc.id}
                  accept=".pdf,image/*"
                />
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div className="rounded-[1.35rem] border p-4 sm:p-5" style={{ borderColor: 'rgba(245,158,11,0.25)', background: 'linear-gradient(180deg, rgba(255,251,235,1) 0%, rgba(255,247,237,1) 100%)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(245,158,11,0.14)' }}>
                    <AlertTriangle size={18} className="text-amber-600" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-amber-950">Final approval is required before payment.</p>
                    <p className="text-sm leading-6 text-amber-900/80">Review the policies below, then confirm your acceptance to continue.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {POLICY_LINKS.map((policy) => (
                  <button
                    key={policy.id}
                    type="button"
                    onClick={() => window.open(policy.path, '_blank')}
                    className="rounded-2xl border p-4 text-left transition-colors hover:bg-slate-50"
                    style={{ borderColor: 'rgba(148,163,184,0.16)', background: '#FFFFFF' }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-slate-900">{policy.label}</p>
                        <p className="text-xs mt-1 text-slate-500">Open policy page</p>
                      </div>
                      <ExternalLink size={16} className="text-slate-400 shrink-0" />
                    </div>
                  </button>
                ))}
              </div>

              <label className="flex items-start gap-3 rounded-[1.2rem] sm:rounded-[1.5rem] border p-4 sm:p-5 cursor-pointer" style={{ borderColor: policyAccepted ? 'rgba(79,70,229,0.28)' : 'rgba(148,163,184,0.18)', background: policyAccepted ? 'rgba(79,70,229,0.05)' : 'rgba(255,255,255,0.85)' }}>
                <input
                  type="checkbox"
                  checked={policyAccepted}
                  onChange={e => setPolicyAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[13px] sm:text-sm leading-6 text-slate-700">
                  I have read and understood the Terms & Conditions and No Refund Policy, and I agree to continue with this non-refundable payment.
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <button onClick={handleBack}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'var(--card)', border: '1px solid var(--card-border)', color: 'var(--text)' }}>
              <ChevronLeft size={16} /> Back
            </button>
          )}
          {step < 2 ? (
            <button onClick={handleNext} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'var(--primary)', color: '#fff' }}>
              Next Step <ChevronRight size={16} />
            </button>
          ) : step === 2 ? (
            <button
              onClick={() => {
                if (!validateDocumentsStep()) return;
                setPolicyAccepted(false);
                setStep(3);

                setTimeout(() => {
                  const formTop = document.getElementById('application-form-top');
                  if (formTop) {
                    const offset = window.innerWidth < 768 ? 100 : 120;
                    const y = formTop.getBoundingClientRect().top + window.scrollY - offset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }, 50);
              }}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'var(--primary)', color: '#fff', opacity: loading ? 0.7 : 1 }}>
              Next Step <ChevronRight size={16} />
            </button>
          ) : (
            <button onClick={processSubmission} disabled={!policyAccepted || loading}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'var(--primary)', color: '#fff', opacity: !policyAccepted || loading ? 0.7 : 1 }}>
              {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{editId ? 'Saving...' : 'Submitting...'}</>
                : <><span>{editId ? 'Save Changes' : 'Accept & Continue to Payment'}</span><ChevronRight size={16} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
